import { Modal } from ".";
import { selectMethodTypes } from "../../../utils/constant";
import Button from "../button/Button";
import { Caravan, CoinsIcon, Landmark, Mail, Wallet } from "lucide-react";

// Add WechatPay and Alipay SVG icons since lucide-react doesn't have them
const WechatPayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 4C5.36 4 2 6.91 2 10.5C2 12.5 3.05 14.28 4.67 15.47L4 18L6.73 16.5C7.6 16.82 8.53 17 9.5 17C9.67 17 9.84 16.99 10.01 16.98C9.84 16.5 9.75 16 9.75 15.5C9.75 12.46 12.6 10 16 10C16.17 10 16.34 10.01 16.5 10.02C15.92 6.62 13.04 4 9.5 4Z" fill="#07C160"/>
    <path d="M22 15.5C22 13.02 19.76 11 17 11C14.24 11 12 13.02 12 15.5C12 17.98 14.24 20 17 20C17.7 20 18.37 19.86 18.97 19.6L21 20.5L20.5 18.5C21.44 17.67 22 16.63 22 15.5Z" fill="#07C160"/>
  </svg>
);

const AlipayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5Z" fill="#1677FF"/>
    <path d="M12 6.5C10.76 6.5 9.5 7.26 9.5 8.75C9.5 9.8 10.16 10.5 11.08 10.95C10.36 11.19 9 11.69 9 11.69L11.86 13.11C11.86 13.11 10.5 13.5 9 13.75C9.82 14.91 11.27 15.5 12 15.5C13.93 15.5 15.5 14.1 15.5 12.5C15.5 12.5 15.5 11 14 10.5C15 10.11 15.5 9.5 15.5 8.75C15.5 7.26 14.24 6.5 12 6.5Z" fill="white"/>
  </svg>
);

interface SelectMethodTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (methodType: selectMethodTypes) => void;
  title: string;
  currency: string;
  availableMethods?: string[];
  feeApplies?: boolean;
}

export function SelectMethodTypeModal({
  isOpen,
  onClose,
  onSelect,
  title,
  currency,
  availableMethods = ['WALLET', 'BANK_TRANSFER', 'ACH', 'WIRE', 'MOBILE_MONEY', 'ZELLE', 'INTERAC', 'STABLE_CURRENCY', 'WECHAT_PAY', 'ALIPAY'],
  feeApplies
}: SelectMethodTypeModalProps) {
  const normalizedMethods = Array.isArray(availableMethods)
    ? availableMethods.map(m => m.toUpperCase())
    : [availableMethods];

  const showWallet = normalizedMethods.includes('WALLET');
  const showBankTransfer = normalizedMethods.some(m => m.includes('BANK_TRANSFER') || m.includes('BANK') || m.includes('TRANSFER'));
  const showAch = normalizedMethods.includes('ACH');
  const showWire = normalizedMethods.includes('WIRE');
  const showMobileMoney = normalizedMethods.some(m => m.includes('MOBILE_MONEY') || m.includes('MOBILE') || m.includes('MONEY'));
  const showZelle = normalizedMethods.includes("ZELLE");
  const showInterac = normalizedMethods.includes("INTERAC");
  const showStableCurrency = normalizedMethods.includes("STABLE_CURRENCY");
  const showWechatPay = normalizedMethods.includes("WECHAT_PAY");
  const showAlipay = normalizedMethods.includes("ALIPAY"); 

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-6">
          {title} - {currency}
        </h3>

        <div className="space-y-3">
          {showWallet && title !== "Send From" && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("WALLET")}>
              <Wallet className="h-5 w-5" />
              Wallet
            </Button>
          )}

          {showBankTransfer && (
            <Button variant="outline" className="w-full justify-between" onClick={() => onSelect("BANK_TRANSFER")}>
              <span className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Bank Transfer
              </span>
              {/* {feeApplies && title === "Receive To" && (
                <span className="text-xs text-red-500 font-medium">Fee applies</span>
              )} */}
            </Button>
          )}

          {showAch && (
            <Button variant="outline" className="w-full justify-between" onClick={() => onSelect("ACH")}>
              <span className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                ACH
              </span>
            </Button>
          )}

          {showWire && (
            <Button variant="outline" className="w-full justify-between" onClick={() => onSelect("WIRE")}>
              <span className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Wire Transfer
              </span>
            </Button>
          )}

          {showMobileMoney && (
            <Button variant="outline" className="w-full justify-between" onClick={() => onSelect("MOBILE_MONEY")}>
              <span className="flex items-center gap-2">
                <Caravan className="h-5 w-5" />
                Mobile Money
              </span>
              {/* {feeApplies && title === "Receive To" && (
                <span className="text-xs text-red-500 font-medium">Fee applies</span>
              )} */}
            </Button>
          )}

          {showZelle && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("ZELLE")}>
              <Mail className="h-5 w-5" />
              Zelle
            </Button>
          )}

          {showInterac && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("INTERAC")}>
              <Mail className="h-5 w-5" />
              Interac
            </Button>
          )}

          {showStableCurrency && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("STABLE_CURRENCY")}>
              <CoinsIcon className="h-5 w-5" />
              Stable Coin
            </Button>
          )}

          {showWechatPay && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("WECHAT_PAY")}>
              <WechatPayIcon />
              WeChat Pay
            </Button>
          )}

          {showAlipay && (
            <Button variant="outline" className="w-full justify-center gap-2" onClick={() => onSelect("ALIPAY")}>
              <AlipayIcon />
              Alipay
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}